/**
 * Custom node types for the Agent Flow Canvas.
 *
 * Node types:
 * - trigger:       Entry point (user message, event, schedule)
 * - agent:         LLM-powered agent (non-deterministic)
 * - tool:          Entity function or FLEX script (deterministic)
 * - condition:     Branch based on expression
 * - parallel:      Fork into parallel branches
 * - humanInput:    Pause for human approval
 * - merge:         Merge parallel branches
 */

import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Box, Typography, Chip } from '@mui/material';
import {
  PlayArrow as TriggerIcon,
  SmartToy as AgentIcon,
  Code as ToolIcon,
  CallSplit as ConditionIcon,
  AccountTree as ParallelIcon,
  Person as HumanIcon,
  CallMerge as MergeIcon,
} from '@mui/icons-material';

const NODE_STYLES = {
  trigger:    { color: '#22c55e', bgLight: 'rgba(34,197,94,0.08)',  bgDark: 'rgba(34,197,94,0.15)',  icon: TriggerIcon },
  agent:      { color: '#3b82f6', bgLight: 'rgba(59,130,246,0.08)', bgDark: 'rgba(59,130,246,0.15)', icon: AgentIcon },
  tool:       { color: '#10b981', bgLight: 'rgba(16,185,129,0.08)', bgDark: 'rgba(16,185,129,0.15)', icon: ToolIcon },
  condition:  { color: '#f59e0b', bgLight: 'rgba(245,158,11,0.08)', bgDark: 'rgba(245,158,11,0.15)', icon: ConditionIcon },
  parallel:   { color: '#8b5cf6', bgLight: 'rgba(139,92,246,0.08)', bgDark: 'rgba(139,92,246,0.15)', icon: ParallelIcon },
  humanInput: { color: '#ef4444', bgLight: 'rgba(239,68,68,0.08)',  bgDark: 'rgba(239,68,68,0.15)',  icon: HumanIcon },
  merge:      { color: '#6366f1', bgLight: 'rgba(99,102,241,0.08)', bgDark: 'rgba(99,102,241,0.15)', icon: MergeIcon },
};

const BaseNode = memo(({ data, selected, type }) => {
  const style = NODE_STYLES[type] || NODE_STYLES.tool;
  const Icon = style.icon;
  const isDark = data.isDark !== false;

  return (
    <Box sx={{
      minWidth: 160,
      maxWidth: 220,
      border: `2px solid ${selected ? style.color : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
      borderRadius: 2,
      bgcolor: isDark ? style.bgDark : style.bgLight,
      boxShadow: selected ? `0 0 0 2px ${style.color}40` : 'none',
      overflow: 'hidden',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        px: 1.5, py: 0.75,
        bgcolor: `${style.color}15`,
        borderBottom: `1px solid ${style.color}30`,
      }}>
        <Icon sx={{ fontSize: 16, color: style.color }} />
        <Typography variant="caption" sx={{ color: style.color, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
          {type}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{
          color: isDark ? '#e5e7eb' : '#1f2937',
          fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3,
          mb: 0.25,
        }}>
          {data.label || 'Untitled'}
        </Typography>
        {data.description && (
          <Typography variant="caption" sx={{
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '0.7rem', lineHeight: 1.2,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {data.description}
          </Typography>
        )}
        {data.ref && (
          <Chip label={data.ref} size="small" sx={{
            mt: 0.5, height: 18, fontSize: '0.6rem', fontFamily: 'monospace',
            bgcolor: `${style.color}20`, color: style.color,
          }} />
        )}
      </Box>

      {/* Handles */}
      {type !== 'trigger' && (
        <Handle type="target" position={Position.Top}
          style={{ background: style.color, width: 8, height: 8, border: '2px solid #fff' }} />
      )}
      <Handle type="source" position={Position.Bottom}
        style={{ background: style.color, width: 8, height: 8, border: '2px solid #fff' }} />

      {/* Condition gets extra handles for branches */}
      {type === 'condition' && (
        <>
          <Handle type="source" position={Position.Right} id="false"
            style={{ background: '#ef4444', width: 8, height: 8, border: '2px solid #fff', top: '50%' }} />
        </>
      )}
    </Box>
  );
});

// Export individual node types for React Flow
export const TriggerNode = memo((props) => <BaseNode {...props} type="trigger" />);
export const AgentNode = memo((props) => <BaseNode {...props} type="agent" />);
export const ToolNode = memo((props) => <BaseNode {...props} type="tool" />);
export const ConditionNode = memo((props) => <BaseNode {...props} type="condition" />);
export const ParallelNode = memo((props) => <BaseNode {...props} type="parallel" />);
export const HumanInputNode = memo((props) => <BaseNode {...props} type="humanInput" />);
export const MergeNode = memo((props) => <BaseNode {...props} type="merge" />);

// Node type registry for React Flow
export const nodeTypes = {
  trigger: TriggerNode,
  agent: AgentNode,
  tool: ToolNode,
  condition: ConditionNode,
  parallel: ParallelNode,
  humanInput: HumanInputNode,
  merge: MergeNode,
};

// For the toolbar — available node types users can add
export const NODE_PALETTE = [
  { type: 'trigger', label: 'Trigger', description: 'Entry point (message, event, schedule)' },
  { type: 'agent', label: 'Agent', description: 'LLM-powered reasoning (non-deterministic)' },
  { type: 'tool', label: 'Tool / Script', description: 'Entity function or FLEX script (deterministic)' },
  { type: 'condition', label: 'Condition', description: 'Branch based on expression' },
  { type: 'parallel', label: 'Parallel', description: 'Fork into parallel branches' },
  { type: 'humanInput', label: 'Human Approval', description: 'Pause for human input' },
  { type: 'merge', label: 'Merge', description: 'Merge parallel branches' },
];

export default NODE_STYLES;
